import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';

import { useAppDispatch } from '@/Application/Hooks/Redux';

import { setIsCencoPayTimeOut } from '../store/slices/billing';

import { useGtmBillingProduct } from './use-gtm-billing';

function useCountdownTimer(transactionCountdown: number) {
  const { cencoPayExpiredQR } = useGtmBillingProduct();
  const dispatch = useAppDispatch();

  const difference = useMemo(
    () => calculateDifferenceTime(transactionCountdown),
    [transactionCountdown]
  );

  const [time, setTime] = useState(difference);

  useEffect(() => {
    let timerInterval: NodeJS.Timeout;

    if (time > 0) {
      timerInterval = setInterval(() => {
        setTime((prevTime) => prevTime - 1);
      }, 1000);
    } else {
      setTime(0);
      dispatch(setIsCencoPayTimeOut(true));
      cencoPayExpiredQR();
    }

    return () => {
      clearInterval(timerInterval);
    };
  }, [cencoPayExpiredQR, dispatch, time]);

  const resetTimer = (newTime: number) => {
    setTime(newTime);
  };

  const hours = Math.floor(time / 3600);
  const minutes = Math.floor((time % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (time % 60).toString().padStart(2, '0');
  const hasHours = hours > 0;
  const timeInMinutesAndSeconds = `${minutes}:${seconds}`;
  const timeInHoursAndMinutes = `${hours}:${minutes}:${seconds}`;

  return {
    time: {
      seconds: time,
      formatted: hasHours ? timeInHoursAndMinutes : timeInMinutesAndSeconds
    },
    resetTimer
  };
}

export function calculateDifferenceTime(transactionCountdown: number): number {
  if (!transactionCountdown) return 0;

  const timezone = 'America/Santiago';
  moment.tz.setDefault(timezone);

  const currentTime = moment();
  const endTime = moment(transactionCountdown);

  if (!endTime.isValid()) return 0;

  const differenceInSeconds = endTime.diff(currentTime, 'seconds');

  return differenceInSeconds;
}

export default useCountdownTimer;
